import {
  AfterViewInit,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { MapConfig } from 'src/app/_shared/config/map.config';
import { PropertyService as WorkspacePropertyService } from 'src/app/workspace/service/property.service';
import { MapState } from 'src/app/workspace/state/map.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import constants from 'src/constant.json';
import { SearchBarFacade } from '../search-bar.facade';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() addressType: string;
  @ViewChild('searchField') searchField: any;

  constant: any = constants;
  autoCompleteInput = '';
  queryWait: boolean;
  subpremise: string;

  onDestroyNotifier$ = new Subject<void>();

  private lastHandledSearch = '';
  private destroyed = false;
  private searchRequestId = 0;

  private autocomplete: google.maps.places.Autocomplete | null = null;
  private autocompleteListener: google.maps.MapsEventListener | null = null;

  constructor(
    private searchBarFacade: SearchBarFacade,
    private workspacePropertyService: WorkspacePropertyService,
    private mapConfig: MapConfig,
    private mapState: MapState,
    private propertyState: PropertyState,
    private route: ActivatedRoute,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.searchBarFacade
      .getFullAddress()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((address) => {
        if (address && address !== this.autoCompleteInput) {
          this.autoCompleteInput = address;
        }
      });
  }

  ngAfterViewInit(): void {
    this.getPlaceAutocomplete();

    // Defer the initial URL search until the current view check finishes.
    Promise.resolve().then(() => {
      if (this.destroyed) {
        return;
      }

      this.route.queryParamMap
        .pipe(
          map((params) => (params.get('search') || '').trim()),
          distinctUntilChanged(),
          takeUntil(this.onDestroyNotifier$)
        )
        .subscribe((search) => {
          if (!search) {
            this.lastHandledSearch = '';

            // Ignore any pending result for the previous URL.
            this.searchRequestId++;
            return;
          }

          if (search === this.lastHandledSearch) {
            return;
          }

          this.lastHandledSearch = search;
          this.autoCompleteInput = search;
          this.invokeEvent();
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.searchRequestId++;

    if (this.autocompleteListener) {
      this.autocompleteListener.remove();
      this.autocompleteListener = null;
    }

    if (this.autocomplete) {
      this.autocomplete.unbindAll();
      this.autocomplete = null;
    }

    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  getPlaceAutocomplete(): void {
    if (
      this.destroyed ||
      this.autocomplete ||
      !this.searchField ||
      !this.searchField.nativeElement
    ) {
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(
      this.searchField.nativeElement,
      {
        componentRestrictions: { country: 'US' },
        types: ['geocode'],
      }
    );

    this.autocompleteListener = this.autocomplete.addListener(
      'place_changed',
      () => {
        if (this.destroyed || !this.autocomplete) {
          return;
        }

        const place = this.autocomplete.getPlace();

        this.ngZone.run(() => {
          this.invokeEvent(place);
        });
      }
    );
  }

  invokeEvent(place?: google.maps.places.PlaceResult): void {
    if (this.destroyed) {
      return;
    }

    const search = (
      place && place.formatted_address
        ? place.formatted_address
        : this.autoCompleteInput || ''
    ).trim();

    if (!search) {
      return;
    }

    this.autoCompleteInput = search;

    if (search.startsWith('#')) {
      // Invalidate any pending address search.
      this.searchRequestId++;

      const mlsNumber = search.substring(1).trim();

      if (mlsNumber) {
        this.workspacePropertyService.fetchListingsByMlsNumber(mlsNumber);
      }

      return;
    }

    // Works for URL searches, the Go button, and autocomplete selections.
    this.getPlaceFromGeocoder();
  }

  getPlaceFromGeocoder(): void {
    const address = (this.autoCompleteInput || '').trim();

    if (!address || this.destroyed) {
      return;
    }

    const requestId = ++this.searchRequestId;

    new google.maps.Geocoder().geocode(
      { address },
      (results, status) => {
        this.ngZone.run(() => {
          // A newer search takes precedence over this result.
          if (
            this.destroyed ||
            requestId !== this.searchRequestId
          ) {
            return;
          }

          if (
            status !== google.maps.GeocoderStatus.OK ||
            !results ||
            !results.length
          ) {
            if (this.lastHandledSearch === address) {
              this.lastHandledSearch = '';
            }

            console.warn('Address search failed:', status);
            return;
          }

          const resolvedPlace = results[0];

          let routeName = '';
          let subPremise = '';
          let streetNumber = '';
          let city = '';
          let state = '';
          let zip = '';

          (resolvedPlace.address_components || []).forEach((component) => {
            const types = component.types;

            if (types.includes('route')) {
              routeName = component.short_name;
            }

            if (types.includes('subpremise')) {
              subPremise = component.short_name;
            }

            if (types.includes('street_number')) {
              streetNumber = component.short_name;
            }

            if (types.includes('locality')) {
              city = component.short_name;
            } else if (!city && types.includes('sublocality')) {
              city = component.short_name;
            }

            if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            }

            if (types.includes('postal_code')) {
              zip = component.short_name;
            }
          });

          const formattedAddress = (
            resolvedPlace.formatted_address || address
          ).trim();

          // Set this before setAddressObj() writes the formatted URL,
          // so that navigation does not start the same search again.
          this.lastHandledSearch = formattedAddress;
          this.autoCompleteInput = formattedAddress;

          if (streetNumber && routeName) {
            const addressObj: any = {
              StreetAddress: `${streetNumber} ${routeName}`,
              City: city,
              State: state,
              PostalCode: zip,
              logInput: true,
            };

            if (subPremise) {
              const normalizedUnit = subPremise.toLowerCase();
              const unitPrefixes: string[] =
                this.constant.UNIT_PREFIX || [];

              const hasUnitPrefix = unitPrefixes.some((prefix) =>
                normalizedUnit.includes(prefix.toLowerCase())
              );

              addressObj.StreetAddress += hasUnitPrefix
                ? ` ${subPremise}`
                : ` # ${subPremise}`;
            }

            this.propertyState.propertyDetailsSheetInput = addressObj;
            this.mapState.mapZoomValue = this.mapConfig.streetLevelZoom;
          } else {
            this.mapState.mapZoomValue = this.mapConfig.defaultZoom;
          }

          this.searchBarFacade.setAddressObj(resolvedPlace);
        });
      }
    );
  }

  loadPropertyDetailsForListing(listing: any): void {
    if (!listing || !listing.StdAddress) {
      return;
    }

    const addressObj: any = {
      StreetAddress: listing.StdAddress.UnparsedAddress,
      City: listing.StdAddress.City,
      State: listing.StdAddress.StateOrProvince,
      PostalCode: listing.StdAddress.PostalCode,
      logInput: true,
    };

    this.propertyState.propertyDetailsSheetInput = addressObj;
    this.mapState.mapZoomValue = this.mapConfig.streetLevelZoom;
  }
}